import { z } from "zod";

import { getPatientMedicationsResponseSchema } from "@/shared/agent-api/responses";
import { AppointmentStatus, AppointmentType } from "@/shared/models/appointment";
import { RelationshipToSubscriber } from "@/shared/models/coverage";
import {
  insuranceAuthorizationReadSchema,
  insurancePolicySnapshotSchema,
  schedulingEligibilitySnapshotSchema,
  SchedulingInsuranceOperation,
} from "@/shared/models/insurance-evidence";
import { SchedulingPriority } from "@/shared/models/referral";

const patientOrderTypeSchema = z.enum(["consult", "lab", "imaging", "prescription", "procedure", "other"]);

const isoDateSchema = z.string().refine(
  (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

    const parsed = new Date(`${value}T00:00:00.000Z`);

    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  },
  { message: "Expected a valid date in YYYY-MM-DD format" }
);

const northAmericanPhoneSchema = z
  .string()
  .transform((value) => value.replace(/\D/g, ""))
  .transform((value) => (value.length === 11 && value.startsWith("1") ? value.slice(1) : value))
  .refine((value) => /^\d{10}$/.test(value), "Expected a 10-digit North American phone number")
  .transform((value) => `+1${value}`);

export const integrationFixtureKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(160)
  .regex(/^[a-z0-9][a-z0-9._/-]*$/, "Use lowercase letters, numbers, '.', '_', '-', or '/'");

export const integrationFixturePatientSchema = z.object({
  givenName: z.string().trim().min(1),
  familyName: z.string().trim().min(1),
  dateOfBirth: isoDateSchema,
  phoneNumber: northAmericanPhoneSchema,
});

const integrationFixtureCoverageFields = {
  ehrId: z.string().trim().min(1).optional(),
  key: integrationFixtureKeySchema,
  customerPayer: z.string().trim().min(1).nullable().default(null),
  relationshipToSubscriber: z.nativeEnum(RelationshipToSubscriber).default(RelationshipToSubscriber.SELF),
  order: z.number().int().positive(),
};

const integrationFixtureCoverageTextSchema = z.string().trim().min(1);

export const integrationFixtureCoverageSchema = z.union([
  z.object({
    ...integrationFixtureCoverageFields,
    coverageType: z.literal("Self Pay"),
    payer: integrationFixtureCoverageTextSchema.nullable().default(null),
    plan: integrationFixtureCoverageTextSchema.nullable().default(null),
    memberId: integrationFixtureCoverageTextSchema.nullable().default(null),
    lineOfBusiness: integrationFixtureCoverageTextSchema.nullable().default(null),
  }),
  z.object({
    ...integrationFixtureCoverageFields,
    coverageType: z.literal("Health Insurance").default("Health Insurance"),
    payer: integrationFixtureCoverageTextSchema,
    plan: integrationFixtureCoverageTextSchema,
    memberId: integrationFixtureCoverageTextSchema,
    lineOfBusiness: integrationFixtureCoverageTextSchema.default("Commercial"),
  }),
]);

/** Synthetic adapter inputs, never precomputed insurance requirements or clearance. */
const integrationFixtureInsurancePolicySchema = insurancePolicySnapshotSchema.extend({
  schedulingEligibility: schedulingEligibilitySnapshotSchema,
});

const integrationFixtureInsuranceCoverageReadSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("complete"),
    policies: z.array(integrationFixtureInsurancePolicySchema).max(100),
  }),
  z.object({
    status: z.literal("partial"),
    policies: z.array(integrationFixtureInsurancePolicySchema).max(100),
  }),
  z.object({
    status: z.literal("failed"),
    reason: z.enum(["unavailable", "invalid_response"]),
  }),
]);

export enum IntegrationFixtureMutationOutcome {
  SUCCEED = "succeed",
  FAIL = "fail",
}

export enum IntegrationFixtureAvailabilityMode {
  EXPLICIT = "explicit",
  RECURRING = "recurring",
}

export enum IntegrationFixturePatientLookup {
  LOCAL_AND_EHR = "local_and_ehr",
  EHR_ONLY = "ehr_only",
  AMBIGUOUS_EHR = "ambiguous_ehr",
}

export enum IntegrationFixtureLocalCoverageStatus {
  COMPLETE = "complete",
  PARTIAL = "partial",
  FAILED = "failed",
}

export const integrationFixtureEhrStateSchema = z.object({
  patientCaseCreation: z.nativeEnum(IntegrationFixtureMutationOutcome).optional(),
  bookingOutcome: z.nativeEnum(IntegrationFixtureMutationOutcome).optional(),
  coverageByOperation: z
    .object({
      [SchedulingInsuranceOperation.ELIGIBILITY]: integrationFixtureInsuranceCoverageReadSchema.optional(),
      [SchedulingInsuranceOperation.AVAILABILITY]: integrationFixtureInsuranceCoverageReadSchema.optional(),
      [SchedulingInsuranceOperation.BOOKING]: integrationFixtureInsuranceCoverageReadSchema.optional(),
      [SchedulingInsuranceOperation.RESCHEDULING]: integrationFixtureInsuranceCoverageReadSchema.optional(),
    })
    .strict()
    .optional(),
  patientLookup: z.nativeEnum(IntegrationFixturePatientLookup).default(IntegrationFixturePatientLookup.LOCAL_AND_EHR),
  localCoverageStatus: z
    .nativeEnum(IntegrationFixtureLocalCoverageStatus)
    .default(IntegrationFixtureLocalCoverageStatus.COMPLETE),
  coverage: integrationFixtureInsuranceCoverageReadSchema,
  authorizations: insuranceAuthorizationReadSchema,
});

export type IntegrationFixtureEhrState = z.infer<typeof integrationFixtureEhrStateSchema>;
export type IntegrationFixtureInsurancePolicy = z.infer<typeof integrationFixtureInsurancePolicySchema>;

// Referral-authorization tracking uses the existing prior_authorization storage model.
const integrationFixtureReferralAuthorizationSchema = z.object({
  key: integrationFixtureKeySchema,
  authorizationNumber: z.string().trim().min(1).nullable(),
  authorizationStatus: z.enum(["approved", "pending", "denied"]).nullable(),
  requestId: z.string().trim().min(1).nullable().optional(),
  numberOfServices: z.number().int().nonnegative().nullable().optional(),
  startDate: isoDateSchema.nullable(),
  endDate: isoDateSchema.nullable(),
});

export const integrationFixtureAppointmentSchema = z
  .object({
    key: integrationFixtureKeySchema,
    practitionerId: z.string().uuid().optional(),
    practitionerEhrId: z.string().trim().min(1).optional(),
    locationId: z.string().uuid().optional(),
    locationEhrId: z.string().trim().min(1).optional(),
    start: z.string().datetime({ offset: true }),
    end: z.string().datetime({ offset: true }),
    type: z.nativeEnum(AppointmentType).default(AppointmentType.FOLLOW_UP),
    status: z.nativeEnum(AppointmentStatus).default(AppointmentStatus.SCHEDULED),
    ehrAppointmentTypeId: z.string().trim().min(1).nullable().default(null),
    ehrAppointmentTypeName: z.string().trim().min(1).nullable().default(null),
    reason: z.string().trim().min(1).nullable().default(null),
  })
  .superRefine((appointment, ctx) => {
    if ((appointment.practitionerId === undefined) === (appointment.practitionerEhrId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["practitionerId"],
        message: "Specify exactly one of practitionerId or practitionerEhrId",
      });
    }

    if ((appointment.locationId === undefined) === (appointment.locationEhrId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["locationId"],
        message: "Specify exactly one of locationId or locationEhrId",
      });
    }

    const durationMs = new Date(appointment.end).getTime() - new Date(appointment.start).getTime();

    if (durationMs <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["end"], message: "Appointment end must follow start" });
    } else if (durationMs < 60_000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["end"],
        message: "Appointment duration must be at least one minute",
      });
    }
  });

export const integrationFixtureAvailabilitySlotSchema = z
  .object({
    key: integrationFixtureKeySchema,
    practitionerId: z.string().uuid().optional(),
    practitionerEhrId: z.string().trim().min(1).optional(),
    locationId: z.string().uuid().optional(),
    locationEhrId: z.string().trim().min(1).optional(),
    start: z.string().datetime({ offset: true }),
    end: z.string().datetime({ offset: true }),
    requestAppointmentTypeEhrIds: z.array(z.string().trim().min(1)).min(1).max(10),
    returnedAppointmentTypeEhrId: z.string().trim().min(1),
    availableCount: z.number().int().positive().default(1),
  })
  .superRefine((slot, ctx) => {
    if ((slot.practitionerId === undefined) === (slot.practitionerEhrId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["practitionerId"],
        message: "Specify exactly one of practitionerId or practitionerEhrId",
      });
    }

    if ((slot.locationId === undefined) === (slot.locationEhrId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["locationId"],
        message: "Specify exactly one of locationId or locationEhrId",
      });
    }

    if (new Set(slot.requestAppointmentTypeEhrIds).size !== slot.requestAppointmentTypeEhrIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["requestAppointmentTypeEhrIds"],
        message: "Requested appointment type IDs must be unique",
      });
    }

    const durationMs = new Date(slot.end).getTime() - new Date(slot.start).getTime();

    if (durationMs <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["end"], message: "Availability end must follow start" });
    } else if (durationMs < 60_000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["end"],
        message: "Availability duration must be at least one minute",
      });
    }
  });

const integrationFixtureAvailabilityModeSchema = z.enum(IntegrationFixtureAvailabilityMode);
const integrationFixtureExplicitAvailabilityMode: `${IntegrationFixtureAvailabilityMode.EXPLICIT}` =
  IntegrationFixtureAvailabilityMode.EXPLICIT;
const integrationFixtureRecurringAvailabilityMode: `${IntegrationFixtureAvailabilityMode.RECURRING}` =
  IntegrationFixtureAvailabilityMode.RECURRING;

export const integrationFixtureAvailabilitySchema = z
  .discriminatedUnion("mode", [
    z.object({
      mode: z
        .literal(integrationFixtureRecurringAvailabilityMode)
        .pipe(integrationFixtureAvailabilityModeSchema.extract(["RECURRING"])),
    }),
    z.object({
      mode: z
        .literal(integrationFixtureExplicitAvailabilityMode)
        .pipe(integrationFixtureAvailabilityModeSchema.extract(["EXPLICIT"])),
      slots: z.array(integrationFixtureAvailabilitySlotSchema).max(200).default([]),
    }),
  ])
  .default({ mode: IntegrationFixtureAvailabilityMode.RECURRING });

export const integrationFixturePatientOrderSchema = z.object({
  key: integrationFixtureKeySchema,
  orderType: patientOrderTypeSchema.default("other"),
  status: z.string().trim().min(1),
  dateOrdered: z.string().datetime({ offset: true }).nullable().default(null),
  description: z.string().trim().min(1),
  documentDescription: z.string().trim().min(1).nullable().default(null),
  departmentEhrId: z.string().trim().min(1).nullable().default(null),
  deletedAt: z.string().datetime({ offset: true }).nullable().default(null),
});

export const integrationFixtureDiagnosisSchema = z.object({
  diagnosisText: z.string().trim().min(1).nullable(),
  diagnosisCode: z.string().trim().min(1).nullable(),
  diagnosisCodeType: z.string().trim().min(1).nullable(),
});

const integrationFixtureReferralReviewedContentSchema = z.object({
  isHospitalFollowUp: z.boolean().nullable().optional(),
});

type FixtureJson = null | boolean | number | string | FixtureJson[] | { [key: string]: FixtureJson };
const fixtureJsonSchema: z.ZodType<FixtureJson> = z.lazy(() =>
  z.union([
    z.null(),
    z.boolean(),
    z.number().finite(),
    z.string(),
    z.array(fixtureJsonSchema),
    z.record(z.string(), fixtureJsonSchema),
  ])
);

// Persist the same task-step records consumed by workflow readers. An omitted task models
// legacy referrals; an empty step list models a modern task awaiting processing.
export const integrationFixtureReferralProcessingSchema = z
  .object({
    steps: z
      .array(
        z.object({
          key: z.string().trim().min(1),
          status: z.enum(["completed", "failed", "paused", "async"]).default("completed"),
          output: z.record(z.string(), fixtureJsonSchema).nullable(),
        })
      )
      .max(40),
  })
  .superRefine(({ steps }, ctx) => {
    if (new Set(steps.map(({ key }) => key)).size !== steps.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["steps"], message: "Processing step keys must be unique" });
    }
  });

export const integrationFixtureReferralSchema = z
  .object({
    key: integrationFixtureKeySchema,
    status: z.string().trim().min(1).default("not_started"),
    reasonForReferral: z.string().trim().min(1),
    schedulingPriority: z.nativeEnum(SchedulingPriority).default(SchedulingPriority.ROUTINE),
    dateOfReferral: isoDateSchema.nullable().default(null),
    receivingLocationId: z.string().uuid().optional(),
    receivingLocationEhrId: z.string().trim().min(1).optional(),
    receivingProviderId: z.string().uuid().optional(),
    receivingProviderEhrId: z.string().trim().min(1).optional(),
    diagnosisList: z.array(integrationFixtureDiagnosisSchema).max(30).nullable().optional(),
    reviewedContent: integrationFixtureReferralReviewedContentSchema.nullable().optional(),
    primaryCoverageKey: integrationFixtureKeySchema.optional(),
    processing: integrationFixtureReferralProcessingSchema.optional(),
    referralAuthorizations: z.array(integrationFixtureReferralAuthorizationSchema).max(20).optional(),
    serviceCodes: z.array(z.string().trim().min(1)).max(30).optional(),
    authorizationNumber: z.string().trim().min(1).nullable().optional(),
    numberOfVisits: z.number().int().nonnegative().nullable().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
  })
  .superRefine((referral, ctx) => {
    const authorizationKeys = referral.referralAuthorizations?.map(({ key }) => key) ?? [];

    if (new Set(authorizationKeys).size !== authorizationKeys.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["referralAuthorizations"],
        message: "Authorization keys must be unique",
      });
    }

    if (referral.receivingLocationId && referral.receivingLocationEhrId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["receivingLocationId"],
        message: "Specify at most one of receivingLocationId or receivingLocationEhrId",
      });
    }

    if (referral.receivingProviderId && referral.receivingProviderEhrId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["receivingProviderId"],
        message: "Specify at most one of receivingProviderId or receivingProviderEhrId",
      });
    }
  });

const integrationFixtureDefinitionFields = {
  fixtureKey: integrationFixtureKeySchema,
  groupId: z.string().uuid(),
  patient: integrationFixturePatientSchema,
  ehrState: integrationFixtureEhrStateSchema.optional(),
  coverages: z.array(integrationFixtureCoverageSchema).max(4).default([]),
  appointments: z.array(integrationFixtureAppointmentSchema).max(20).default([]),
  availability: integrationFixtureAvailabilitySchema,
  patientOrders: z.array(integrationFixturePatientOrderSchema).max(20).default([]),
  patientMedications: getPatientMedicationsResponseSchema.nullable().optional(),
  referrals: z.array(integrationFixtureReferralSchema).max(10).default([]),
};

function validateUniqueResourceKeys(
  fixture: {
    coverages: Array<{ key: string }>;
    appointments: Array<{ key: string }>;
    availability: { mode: "recurring" } | { mode: "explicit"; slots: Array<{ key: string }> };
    patientOrders: Array<{ key: string }>;
    referrals: Array<{ key: string; primaryCoverageKey?: string }>;
  },
  ctx: z.RefinementCtx
): void {
  fixture.referrals.forEach((referral, index) => {
    if (referral.primaryCoverageKey && !fixture.coverages.some(({ key }) => key === referral.primaryCoverageKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["referrals", index, "primaryCoverageKey"],
        message: "Referral coverage must belong to this fixture",
      });
    }
  });
  for (const [path, keys] of [
    ["coverages", fixture.coverages.map(({ key }) => key)],
    ["appointments", fixture.appointments.map(({ key }) => key)],
    [
      "availability.slots",
      fixture.availability.mode === "explicit" ? fixture.availability.slots.map(({ key }) => key) : [],
    ],
    ["patientOrders", fixture.patientOrders.map(({ key }) => key)],
    ["referrals", fixture.referrals.map(({ key }) => key)],
  ] as const) {
    if (new Set(keys).size !== keys.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: `${path} keys must be unique` });
    }
  }
}

export const integrationFixtureDefinitionSchema = z
  .object(integrationFixtureDefinitionFields)
  .superRefine(validateUniqueResourceKeys);

export type IntegrationFixtureDefinitionInput = z.input<typeof integrationFixtureDefinitionSchema>;

export const provisionIntegrationFixtureRequestSchema = z
  .object({ ...integrationFixtureDefinitionFields, leaseId: z.string().uuid() })
  .superRefine(validateUniqueResourceKeys);

export type ProvisionIntegrationFixtureRequest = z.infer<typeof provisionIntegrationFixtureRequestSchema>;

export const releaseIntegrationFixtureLeaseRequestSchema = z.object({
  fixtureKey: integrationFixtureKeySchema,
  groupId: z.string().uuid(),
  leaseId: z.string().uuid(),
});

export type ReleaseIntegrationFixtureLeaseRequest = z.infer<typeof releaseIntegrationFixtureLeaseRequestSchema>;

export const getIntegrationFixtureLeaseRequestSchema = z.object({ groupId: z.string().uuid() });

export type GetIntegrationFixtureLeaseRequest = z.infer<typeof getIntegrationFixtureLeaseRequestSchema>;

export const integrationFixtureLeaseSchema = z.object({
  groupId: z.string().uuid(),
  fixtureKey: integrationFixtureKeySchema,
  leaseId: z.string().uuid(),
  expiresAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});

export type IntegrationFixtureLease = z.infer<typeof integrationFixtureLeaseSchema>;

export const getIntegrationFixtureLeaseResponseSchema = z.object({
  lease: integrationFixtureLeaseSchema.nullable(),
});

export type GetIntegrationFixtureLeaseResponse = z.infer<typeof getIntegrationFixtureLeaseResponseSchema>;

export const getIntegrationFixturePatientCasesRequestSchema = z.object({
  groupId: z.string().uuid(),
  fixtureKey: integrationFixtureKeySchema,
});

export const getIntegrationFixturePatientCasesResponseSchema = z.object({
  patientCases: z.array(
    z.object({
      id: z.string().uuid(),
      caseType: z.string(),
      caller: z.string(),
      reason: z.string(),
      internalNote: z.string(),
    })
  ),
});

export type GetIntegrationFixturePatientCasesRequest = z.infer<typeof getIntegrationFixturePatientCasesRequestSchema>;
export type GetIntegrationFixturePatientCasesResponse = z.infer<typeof getIntegrationFixturePatientCasesResponseSchema>;

export const recoverIntegrationFixtureLeaseRequestSchema = z.object({
  groupId: z.string().uuid(),
  expectedFixtureKey: integrationFixtureKeySchema,
  expectedLeaseId: z.string().uuid(),
  force: z.literal(true),
});

export type RecoverIntegrationFixtureLeaseRequest = z.infer<typeof recoverIntegrationFixtureLeaseRequestSchema>;

export const provisionIntegrationFixtureResponseSchema = z.object({
  fixtureId: z.string().uuid(),
  fixtureKey: integrationFixtureKeySchema,
  generation: z.number().int().positive(),
  patientId: z.string().uuid(),
  coverageIds: z.record(z.string(), z.string().uuid()),
  appointmentIds: z.record(z.string(), z.string().uuid()),
  availabilitySlotIds: z.record(z.string(), z.string().uuid()).default({}),
  patientOrderIds: z.record(z.string(), z.string().uuid()),
  referralIds: z.record(z.string(), z.string().uuid()),
  referralSectionIds: z.record(z.string(), z.string().uuid()).default({}),
});

export type ProvisionIntegrationFixtureResponse = z.infer<typeof provisionIntegrationFixtureResponseSchema>;

export const releaseIntegrationFixtureLeaseResponseSchema = z.object({ released: z.literal(true) });

export type ReleaseIntegrationFixtureLeaseResponse = z.infer<typeof releaseIntegrationFixtureLeaseResponseSchema>;

export const recoverIntegrationFixtureLeaseResponseSchema = z.object({ recovered: z.boolean() });

export type RecoverIntegrationFixtureLeaseResponse = z.infer<typeof recoverIntegrationFixtureLeaseResponseSchema>;
